// Systemic Substrates hub · v19.46
// Shows the 7 substrates (Building · Soil · Agriculture · Water · Energy ·
// Electrical · Industrial) with entry-density counters, active/inactive
// status, and per-substrate expandable sections. Read-only in this cut;
// propose/verify/dispute flows land through /api/substrates/* endpoints
// and are surfaced here as counters + status.
//
// Per Architect directive (Feb 16, 2026): "Triangulate and fact check
// before we add to them. Leave it open so professionals can prove errors
// and we can verify + use that to continue searching for solutions."
import { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle, Loader2, ChevronDown, ChevronRight, ScrollText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SUBSTRATE_ICONS = {
  building: "🏗",
  soil: "🌱",
  agriculture: "🌾",
  water: "💧",
  energy: "⚡",
  electrical: "🔌",
  industrial: "⚙",
};

export default function SystemicSubstrates() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [expanded, setExpanded] = useState({});
  const [entries, setEntries] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/substrates/list`);
        if (!r.ok) throw new Error(`http ${r.status}`);
        const d = await r.json();
        if (!cancelled) setState({ loading: false, data: d, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: String(e) });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async (slug) => {
    const isOpen = !!expanded[slug];
    setExpanded((s) => ({ ...s, [slug]: !isOpen }));
    if (!isOpen && !entries[slug]) {
      try {
        const r = await fetch(`${API}/api/substrates/${slug}/entries`);
        const d = await r.json();
        setEntries((s) => ({ ...s, [slug]: d }));
      } catch (e) {
        setEntries((s) => ({ ...s, [slug]: { error: String(e) } }));
      }
    }
  };

  if (state.loading) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500" data-testid="substrates-loading">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading substrates…
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="py-20 text-center text-red-400" data-testid="substrates-error">
        <AlertCircle className="h-4 w-4 inline mr-2" /> {state.error || "no data"}
      </div>
    );
  }

  const { substrates, active_count, total, min_citations_per_proposal, principle } = state.data;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 space-y-8" data-testid="systemic-substrates-page">
      <header className="border-b border-zinc-800 pb-6">
        <div className="text-[10px] uppercase tracking-[0.4em] text-emerald-400 mb-2">
          Operate · Systemic Substrates
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl leading-tight">
          Seven substrates. <span className="text-zinc-500">{active_count} of {total} active.</span>
        </h1>
        <p className="mt-4 text-zinc-400 text-sm leading-relaxed max-w-3xl">
          {principle}
        </p>
        <div className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-amber-300 border border-amber-500/40 px-3 py-1.5">
          <ScrollText className="h-3 w-3" />
          Minimum {min_citations_per_proposal} independent citations per proposal
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4">
        {substrates.map((s) => {
          const isOpen = !!expanded[s.slug];
          const entryData = entries[s.slug];
          return (
            <div
              key={s.slug}
              data-testid={`substrate-card-${s.slug}`}
              className={`border ${s.active ? "border-emerald-700/40" : "border-zinc-800"} bg-[#0b0b0d]`}
            >
              <button
                onClick={() => toggle(s.slug)}
                data-testid={`substrate-toggle-${s.slug}`}
                className="w-full flex items-start justify-between gap-4 p-5 text-left hover:bg-[#0d0d10] transition-colors"
              >
                <div className="flex items-start gap-4 flex-1">
                  <div className="text-2xl leading-none pt-1 select-none opacity-80">
                    {SUBSTRATE_ICONS[s.slug] || "◆"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-heading text-lg text-zinc-100">{s.label}</div>
                      {s.active ? (
                        <span
                          data-testid={`substrate-status-${s.slug}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-emerald-400 border border-emerald-500/50 px-2 py-0.5"
                        >
                          <CheckCircle2 className="h-3 w-3" /> Active
                        </span>
                      ) : (
                        <span
                          data-testid={`substrate-status-${s.slug}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-zinc-500 border border-zinc-700 px-2 py-0.5"
                        >
                          <AlertCircle className="h-3 w-3" /> Thin · needs triangulation
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.why}</div>
                    <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-zinc-500">
                      <span data-testid={`substrate-count-curated-${s.slug}`}>
                        <span className="text-zinc-300 font-mono">{s.counts.curated_kb}</span> curated
                      </span>
                      <span data-testid={`substrate-count-verified-${s.slug}`}>
                        <span className="text-emerald-400 font-mono">{s.counts.community_verified}</span> community-verified
                      </span>
                      <span data-testid={`substrate-count-pending-${s.slug}`}>
                        <span className="text-amber-300 font-mono">{s.counts.pending_triangulation}</span> pending triangulation
                      </span>
                      <span data-testid={`substrate-count-disputes-${s.slug}`}>
                        <span className="text-red-400 font-mono">{s.counts.open_disputes}</span> open disputes
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-zinc-500 pt-1">
                  {isOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-zinc-800 p-5 space-y-4" data-testid={`substrate-detail-${s.slug}`}>
                  {!entryData && (
                    <div className="text-zinc-500 text-sm inline-flex items-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin" /> Loading entries…
                    </div>
                  )}
                  {entryData?.error && (
                    <div className="text-red-400 text-sm">{entryData.error}</div>
                  )}
                  {entryData?.curated_entries && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                        Curated · architect-vetted at inception
                      </div>
                      <ul className="space-y-2">
                        {entryData.curated_entries.slice(0, 20).map((e, i) => (
                          <li
                            key={i}
                            data-testid={`substrate-entry-${s.slug}-${i}`}
                            className="border-l-2 border-zinc-700 pl-3 text-sm text-zinc-300"
                          >
                            <div className="text-zinc-100 font-medium">{e.name}</div>
                            <div className="text-zinc-400 leading-relaxed mt-0.5">{e.why}</div>
                            <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-emerald-500/70">
                              Relieves · {e.relieves}
                            </div>
                          </li>
                        ))}
                      </ul>
                      {entryData.curated_entries.length > 20 && (
                        <div className="text-[11px] text-zinc-600 mt-2">
                          + {entryData.curated_entries.length - 20} more curated entries
                        </div>
                      )}
                    </div>
                  )}
                  {entryData?.community_verified_entries?.length > 0 && (
                    <div className="pt-4 border-t border-zinc-800">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-2">
                        Community-verified · chain-anchored
                      </div>
                      <ul className="space-y-2">
                        {entryData.community_verified_entries.map((e, i) => (
                          <li key={i} className="border-l-2 border-emerald-700 pl-3 text-sm text-zinc-300">
                            <div className="text-zinc-100 font-medium">{e.name}</div>
                            <div className="text-zinc-400 leading-relaxed mt-0.5">{e.why}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {entryData?.pending_triangulation?.length > 0 && (
                    <div className="pt-4 border-t border-zinc-800">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-2">
                        Pending triangulation · not yet released
                      </div>
                      <ul className="space-y-2">
                        {entryData.pending_triangulation.map((e, i) => (
                          <li key={i} className="border-l-2 border-amber-500 pl-3 text-sm text-zinc-300">
                            <div className="text-zinc-100 font-medium">{e.name}</div>
                            <div className="text-zinc-400 leading-relaxed mt-0.5">{e.why}</div>
                            <div className="text-[10px] text-zinc-500 mt-1">
                              {e.citation_count} citation{e.citation_count !== 1 ? "s" : ""} attached
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <footer className="text-center text-[11px] text-zinc-600 pt-8 border-t border-zinc-900">
        Verification is open. Professionals with domain expertise can dispute any entry through
        <code className="mx-1 text-zinc-500">POST /api/substrates/dispute</code> — dispute lives on the never-harm chain.
      </footer>
    </div>
  );
}
